import { useMemo } from "react";
import { CalendarRange, Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n + 1); return d; };

export const PRESETS = [
  { key: "7d", label: "Last 7 days", range: () => [iso(daysAgo(7)), iso(new Date())] },
  { key: "30d", label: "Last 30 days", range: () => [iso(daysAgo(30)), iso(new Date())] },
  { key: "90d", label: "Last 90 days", range: () => [iso(daysAgo(90)), iso(new Date())] },
  { key: "qtr", label: "This quarter", range: () => { const n = new Date(); return [iso(new Date(Date.UTC(n.getFullYear(), Math.floor(n.getMonth() / 3) * 3, 1))), iso(n)]; } },
  { key: "year", label: "This year", range: () => { const n = new Date(); return [iso(new Date(Date.UTC(n.getFullYear(), 0, 1))), iso(n)]; } },
  { key: "custom", label: "Custom dates" },
];

const SEL = "tp-select h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-100";

export function FilterBar({ f, setF, clients, onExport, exporting }) {
  const client = clients.find((c) => c.id === f.client_id);
  const pick = (preset) => {
    const p = PRESETS.find((x) => x.key === preset);
    setF((s) => ({ ...s, preset, ...(p.range ? { start: p.range()[0], end: p.range()[1] } : {}) }));
  };
  const accounts = useMemo(() => client?.accounts || [], [client]);
  const lines = useMemo(() => client?.service_lines || [], [client]);
  const btn = (kind, Icon, label) => (
    <button onClick={() => onExport(kind)} disabled={!!exporting} data-testid={`analytics-export-${kind}-button`}
      className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 ${kind === "pdf" ? "bg-indigo-600 hover:bg-indigo-500 text-white" : "border border-slate-200 bg-white hover:bg-slate-50 text-slate-700"}`}>
      {exporting === kind ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}{label}
    </button>
  );
  return (
    <div className="tp-card p-4 flex flex-col xl:flex-row xl:items-center justify-between gap-3 md:sticky md:top-16 z-20" data-testid="analytics-filter-bar">
      <div className="flex flex-wrap items-center gap-2">
        <CalendarRange className="w-4 h-4 text-slate-400" />
        <select value={f.preset} onChange={(e) => pick(e.target.value)} className={SEL} data-testid="analytics-time-range-picker">
          {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        {f.preset === "custom" && (
          <>
            <input type="date" value={f.start} max={f.end} onChange={(e) => setF((s) => ({ ...s, start: e.target.value }))} className={SEL} data-testid="analytics-start-date" />
            <span className="text-slate-400 text-sm">to</span>
            <input type="date" value={f.end} min={f.start} onChange={(e) => setF((s) => ({ ...s, end: e.target.value }))} className={SEL} data-testid="analytics-end-date" />
          </>
        )}
        <select value={f.client_id} onChange={(e) => setF((s) => ({ ...s, client_id: e.target.value, account_id: "", service_line_id: "" }))} className={SEL} data-testid="analytics-filter-client-select">
          <option value="">All clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={f.account_id} disabled={!client} onChange={(e) => setF((s) => ({ ...s, account_id: e.target.value }))} className={`${SEL} disabled:opacity-50`} data-testid="analytics-filter-account-select">
          <option value="">{client ? "All accounts" : "Account"}</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select value={f.service_line_id} disabled={!client} onChange={(e) => setF((s) => ({ ...s, service_line_id: e.target.value }))} className={`${SEL} disabled:opacity-50`} data-testid="analytics-filter-service-line-select">
          <option value="">{client ? "All service lines" : "Service line"}</option>
          {lines.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select value={f.job_type} onChange={(e) => setF((s) => ({ ...s, job_type: e.target.value }))} className={SEL} data-testid="analytics-filter-job-type-select">
          <option value="">All job types</option><option value="fte">Full Time</option><option value="contract">Contract</option>
        </select>
      </div>
      <div className="flex flex-wrap gap-2">
        {btn("pdf", FileText, "Leadership PDF")}
        {btn("excel", FileSpreadsheet, "Excel")}
        {btn("csv", Download, "CSV")}
      </div>
    </div>
  );
}
